import { Routes, useJourneyNavigation } from '../../../routes';
import { LayerRenderEvent } from '../../../layers/LayerManager';
import { LayerWidget } from '../../../layers/LayerWidget';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { BreadCrumbWidget } from '../../../widgets/header/BreadCrumbWidget';
import { FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { IntegrationKind } from '@journeyapps-platform/types-cerberus';
import { styled } from '../../../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { ENV } from '../../../env';
import { generatePath } from 'react-router-dom';
import { LargeButtonIconWidget } from '../../../widgets/dialog/LargeButtonIconWidget';

const azure_devops = require('../../../../media/azure_devops.png');

namespace S {
  const ICON_SIZE = 70;

  export const IntegrationsContainer = styled.div`
    display: flex;
  `;

  export const Icon = styled(FontAwesomeIcon)`
    width: ${ICON_SIZE}px;
    height: ${ICON_SIZE}px;
    color: white;
  `;
}

//!----------------------------------------------------------------------

interface Integration {
  key: string;
  label: string;
  icon: string | JSX.Element;
  beta?: boolean;
}

export const INTEGRATIONS: Integration[] = [
  {
    key: IntegrationKind.GitHub,
    icon: <S.Icon icon={'fa-brands fa-github' as any} />,
    label: 'GitHub'
  },
  // {
  //   key: IntegrationKind.Git,
  //   icon: <S.Icon icon={'fa-brands fa-git' as any} />,
  //   label: 'Custom Git Repo'
  // },
  {
    key: IntegrationKind.AzureDevops,
    icon: azure_devops,
    label: 'Azure DevOps',
    beta: true
  }
];

//!----------------------------------------------------------------------

export const AddIntegrationDialog: React.FC<{}> = () => {
  const handler = useJourneyNavigation(Routes.CREATE_INTEGRATION);
  const orgStore = useOrgStore();
  const { params, navigate } = handler;

  const submitOrCancel = (event: LayerRenderEvent<Routes.CREATE_INTEGRATION>) => {
    navigate(Routes.MANAGE_INTEGRATIONS, params);
    event.layer.dispose();
  };

  useEffect(() => {
    (async () => {
      await orgStore.resolve(params.org_id);
    })();
  }, []);

  const org = orgStore.org(params.org_id);
  if (!org) {
    return null;
  }

  return (
    <LayerWidget<Routes.CREATE_INTEGRATION>
      options={{ userCanExit: false, withBackdrop: true }}
      renderLayer={(event) => {
        return (
          <IntegrationSelection
            setupIntegration={async (i) => {
              // TODO as we add more of these, we should move these into some kind of handler once we know
              // the abstractions better
              if (i === IntegrationKind.GitHub || i === IntegrationKind.AzureDevops) {
                const url = await org.createIntegration({
                  kind: i,
                  return_url: `${ENV.ACCOUNTS_PORTAL_URL}${generatePath(Routes.MANAGE_INTEGRATIONS, params)}`
                });
                if (url.setup_url) {
                  window.location.replace(url.setup_url);
                } else {
                  submitOrCancel(event);
                }
              }
            }}
            onCancel={() => {
              submitOrCancel(event);
            }}
          />
        );
      }}
    />
  );
};

const IntegrationSelection: React.FC<{
  onCancel: () => any;
  setupIntegration: (integration: IntegrationKind) => any;
}> = (props) => {
  const [integration, setIntegration] = useState<string>();

  return (
    <CenterFormWidget
      noLogo={true}
      primaryLabel={'Add Integration'}
      secondaryLabel="Select an integration"
      orientation={CenterFormOrientation.ROW}
    >
      <BreadCrumbWidget steps={[]} currentStep={0} />
      <FormLayoutWidget
        sections={[
          {
            key: 'integrations',
            elements: [
              <S.IntegrationsContainer>
                {INTEGRATIONS.map((i) => {
                  return (
                    <LargeButtonIconWidget
                      selected={integration === i.key}
                      key={i.key}
                      label={i.label}
                      image={i.icon}
                      beta={i.beta}
                      onSelect={() => {
                        setIntegration(i.key);
                      }}
                    />
                  );
                })}
              </S.IntegrationsContainer>
            ]
          },
          {
            key: 'submit',
            elements: [
              <GroupedButtons
                btns={[
                  {
                    label: 'Cancel',
                    action: (event, loading) => {
                      props.onCancel();
                    }
                  },
                  {
                    label: 'Next',
                    disabled: !integration,
                    action: async (event, loading) => {
                      try {
                        loading(true);
                        await props.setupIntegration(integration as IntegrationKind.GitHub);
                      } finally {
                        loading(false);
                      }
                    }
                  }
                ]}
              />
            ]
          }
        ]}
      />
    </CenterFormWidget>
  );
};
